// The quality control groups one weapon row with the stock variant and the tiers its class
// offers. Every price and every stat on those variants is GCS's bake, so nothing here
// computes.

import type { BakedRow } from "./catalogue";

export interface QualityModifier {
  tier: string;
  caption?: string;
  row: { id: string; name: string; [field: string]: unknown };
}

export interface QualityTable {
  groups: Record<string, Record<string, string>>;
  modifiers: Record<string, QualityModifier>;
}

export interface Tier {
  tier: string;
  label: string;
  caption: string | null;
  row: BakedRow;
}

/** One purchasable item: the row as it stands, and the tiers it may be bought at. */
export interface Stall {
  item: string;
  stock: BakedRow;
  tiers: Tier[];
}

const NO_TABLE: QualityTable = { groups: {}, modifiers: {} };

/** The modifier GCS left attached to a row. */
export function attached(row: BakedRow): string | null {
  const modifiers = row["modifiers"];
  if (!Array.isArray(modifiers) || modifiers.length === 0) return null;
  const id = (modifiers[0] as { id?: unknown }).id;
  return typeof id === "string" ? id : null;
}

/** The tier a row carries, looked up against the closed set the artifact carries. */
export function tierOf(row: BakedRow, table: QualityTable = NO_TABLE): string | null {
  const id = attached(row);
  return id === null ? null : (table.modifiers[id]?.tier ?? null);
}

/** A row that carries quality reads the way GCS writes it: the item, then the modifier. */
export function qualifiedName(row: BakedRow, table: QualityTable = NO_TABLE): string {
  const id = attached(row);
  const modifier = id === null ? undefined : table.modifiers[id];
  return modifier === undefined ? row.description : `${row.description} (${modifier.row.name})`;
}

/** Groups the harvested rows: each stock row followed by the variants sharing its id. */
export function stalls(rows: BakedRow[], table: QualityTable = NO_TABLE): Stall[] {
  const stalls: Stall[] = [];
  const byItem = new Map<string, Stall>();

  for (const row of rows) {
    const id = attached(row);
    if (id === null) {
      const stall = { item: row.id, stock: row, tiers: [] };
      stalls.push(stall);
      byItem.set(row.id, stall);
      continue;
    }
    const modifier = table.modifiers[id];
    const stall = byItem.get(row.id);
    if (modifier === undefined || stall === undefined) {
      throw new Error(`${row.id} carries the unknown modifier ${id}`);
    }
    stall.tiers.push({
      tier: modifier.tier,
      label: label(modifier.tier),
      caption: modifier.caption ?? null,
      row,
    });
  }
  return stalls;
}

/** `very-fine` is the tier the cart stores; `Very Fine` is what the control reads. */
function label(tier: string): string {
  return tier
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function stallFor(stalls: Stall[], item: string): Stall | undefined {
  return stalls.find((stall) => stall.item === item);
}

/** The row a cart line names: the stock row, or the variant carrying the chosen tier. */
export function rowFor(stall: Stall, quality: string | undefined): BakedRow {
  if (quality === undefined) return stall.stock;
  const tier = stall.tiers.find((offered) => offered.tier === quality);
  if (!tier) throw new Error(`${stall.item} is not offered at ${quality}`);
  return tier.row;
}
